//@flow
import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Fab from '@material-ui/core/Fab';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import Tooltip from '@material-ui/core/Tooltip';
import BugReportIcon from '@material-ui/icons/BugReport';

import { submitBugReport } from '../../../services/errorReporter';

const useStyles = makeStyles((theme) => ({
    fab: {
        position: 'fixed',
        bottom: theme.spacing(3),
        right: theme.spacing(3),
        backgroundColor: '#F3C817',
        color: '#3E414A',
        zIndex: 1200,
        '&:hover': {
            backgroundColor: '#E6B800',
        },
    },
    formControl: {
        marginTop: theme.spacing(2),
        minWidth: 200,
    },
    field: {
        marginTop: theme.spacing(2),
    },
}));

export default function ReportBugButton(): React$Node {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [description, setDescription] = useState('');
    const [severity, setSeverity] = useState('error');
    const [screenshotRef, setScreenshotRef] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
    const mountedRef = useRef(true);

    useEffect(() => {
        return () => { mountedRef.current = false; };
    }, []);

    const handleOpen = () => setOpen(true);

    const handleClose = () => {
        if (submitting) return;
        setOpen(false);
    };

    const resetForm = () => {
        setDescription('');
        setSeverity('error');
        setScreenshotRef('');
    };

    const handleSubmit = async () => {
        if (!description.trim()) return;
        setSubmitting(true);
        try {
            const metadata: any = { reportType: 'manual' };
            if (screenshotRef.trim()) metadata.screenshotRef = screenshotRef.trim();

            const ok = await submitBugReport({
                severity,
                errorType: 'UserReport',
                errorMessage: description.trim(),
                metadata,
            });

            if (!mountedRef.current) return;

            if (ok) {
                setSnackbar({ open: true, severity: 'success', message: 'Bug report submitted. Thank you!' });
                resetForm();
                setOpen(false);
            } else {
                setSnackbar({ open: true, severity: 'error', message: 'Failed to submit bug report. Please try again.' });
            }
        } catch (e) {
            if (!mountedRef.current) return;
            setSnackbar({ open: true, severity: 'error', message: 'Failed to submit bug report. Please try again.' });
        } finally {
            if (mountedRef.current) setSubmitting(false);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    return (
        <>
            <Tooltip title="Report a bug" placement="left">
                <Fab
                    aria-label="report bug"
                    className={classes.fab}
                    onClick={handleOpen}
                    size="medium"
                >
                    <BugReportIcon />
                </Fab>
            </Tooltip>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Report a Bug</DialogTitle>
                <DialogContent>
                    <TextField
                        className={classes.field}
                        label="Description"
                        placeholder="What went wrong? Please include the steps to reproduce if possible."
                        multiline
                        rows={5}
                        fullWidth
                        required
                        variant="outlined"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        disabled={submitting}
                    />

                    <FormControl className={classes.formControl} variant="outlined" fullWidth>
                        <InputLabel>Severity</InputLabel>
                        <Select
                            value={severity}
                            label="Severity"
                            onChange={(e) => setSeverity(String(e.target.value))}
                            disabled={submitting}
                        >
                            <MenuItem value="critical">Critical</MenuItem>
                            <MenuItem value="error">Error</MenuItem>
                            <MenuItem value="warning">Warning</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        className={classes.field}
                        label="Screenshot URL (optional)"
                        placeholder="Paste a link to a screenshot if available"
                        fullWidth
                        variant="outlined"
                        value={screenshotRef}
                        onChange={(e) => setScreenshotRef(e.target.value)}
                        disabled={submitting}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        color="primary"
                        variant="contained"
                        disabled={submitting || !description.trim()}
                    >
                        {submitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}
